import { getDeliveryProgressInfo } from './utils';
import { makePrice, Price } from 'lib/core';
import DeliveryFeeProgressStepper from './DeliveryFeeProgressStepper';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { CompactDeliveryProgress } from './CompactDeliveryProgress';
import { AnimatedNumber } from 'components/common/v2';

const getVariantClassName = (
  variant: DeliveryFeeTierProps['variant'],
): {
  container: string;
  caption: string;
  price?: string | undefined;
} => {
  switch (variant) {
    case 'dropdown':
      return {
        container: 'tw-space-y-3',
        caption:
          'tw-text-[11px] tw-text-[#777] tw-leading-none tw-font-normal',
      };

    case 'cart':
      return {
        container:
          'tw-bg-[#fbf1ef] tw-py-4 tw-px-6 tw-rounded-lg tw-w-full tw-gap-4',
        caption: 'tw-leading-snug tw-text-[#666]',
        price: 'tw-text-primary',
      };

    case 'toast':
      return { container: '', caption: '' };

    default:
      return { container: '', caption: '' };
  }
};

export interface DeliveryFeeTierProps {
  total: number;
  previousTotal?: number;
  className?: string;
  variant?: 'dropdown' | 'cart' | 'toast';
  headerOffset?: number;
}

const DeliveryFeeTier = ({
  total,
  previousTotal,
  className,
  variant = 'dropdown',
  headerOffset = 122,
}: DeliveryFeeTierProps) => {
  const { amountAway, nextFee } = getDeliveryProgressInfo(total);
  const { container, caption, price } = getVariantClassName(variant);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (variant !== 'cart') return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      {
        rootMargin: `-${headerOffset - 100}px 0px 0px 0px`,
        threshold: 0,
      },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [headerOffset, variant]);

  return (
    <>
      <div
        ref={sentinelRef}
        className="tw-h-px tw-hidden lg:tw-block"
      />
      <div
        className={cn('tw-flex tw-flex-col', container, className)}
        style={{ top: headerOffset }}
      >
        <div className="tw-flex">
          <div className={cn('tw-mb-0', caption)}>
            {amountAway && amountAway > 0 ? (
              <>
                You're just{' '}
                <strong className={price}>
                  <AnimatedNumber
                    value={amountAway}
                    format={makePrice}
                  />
                </strong>{' '}
                away from{' '}
                <strong className={price}>
                  {nextFee === 0 ? (
                    <strong>FREE</strong>
                  ) : (
                    <Price value={nextFee} />
                  )}
                </strong>{' '}
                delivery!
              </>
            ) : (
              <>
                Steady, lah! Your delivery is now{' '}
                <strong className={price}>FREE</strong>!
              </>
            )}
          </div>
        </div>
        <DeliveryFeeProgressStepper
          total={total}
          previousTotal={previousTotal}
          variant={variant}
        />
      </div>
      {variant === 'cart' && (
        <div
          className={cn(
            'tw-sticky tw-z-50 tw-self-start tw-hidden lg:tw-flex tw-flex-col tw-transition-all tw-duration-300 tw-ease-out tw-bg-[#fbf1ef] tw-rounded-b-lg',
            isStuck
              ? 'tw-translate-y-0 tw-opacity-100'
              : '-tw-translate-y-full tw-pointer-events-none tw-opacity-0',
          )}
          style={{ top: `${headerOffset}px` }}
        >
          <CompactDeliveryProgress total={total} />
        </div>
      )}
    </>
  );
};

export default DeliveryFeeTier;
